import { useState, useEffect } from 'react';
import { UserRole, UserStatus, Location } from '../../types/ticket';
import type { User } from '../../types/ticket';
import './UserDialog.css';

interface UserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (userData: Partial<User>) => void;
}

interface FormData {
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  location: Location | '';
}

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  location?: string;
}

// Group locations by region for better UX
const LOCATION_GROUPS = [
  {
    label: 'United States',
    locations: [
      Location.US_DEARBORN,
      Location.US_HOUSTON,
      Location.US_MELBOURNE,
    ],
  },
  {
    label: 'United Kingdom',
    locations: [
      Location.UK_DAVENTRY,
      Location.UK_DUNTON,
      Location.UK_GLASGOW,
    ],
  },
  {
    label: 'Europe',
    locations: [
      Location.EU_GERMANY,
      Location.EU_GREECE,
      Location.EU_BULGARIA,
    ],
  },
  {
    label: 'Asia Pacific',
    locations: [
      Location.PH_CAINTA,
      Location.OC_AUSTRALIA,
      Location.OC_THAILAND,
    ],
  },
  {
    label: 'Americas',
    locations: [
      Location.NA_MEXICO,
      Location.SA_BRAZIL,
    ],
  },
  {
    label: 'Africa',
    locations: [
      Location.AF_SOUTH_AFRICA,
    ],
  },
];

export const UserDialog: React.FC<UserDialogProps> = ({
  open,
  user,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: UserRole.T1_TECH,
    status: UserStatus.ACTIVE,
    location: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Handle open/close state changes
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsAnimatingOut(false);
      if (user) {
        setFormData({
          name: user.name,
          email: user.email,
          role: user.role,
          status: user.status,
          location: user.location || '',
        });
      } else {
        setFormData({
          name: '',
          email: '',
          role: UserRole.T1_TECH,
          status: UserStatus.ACTIVE,
          location: '',
        });
      }
      setErrors({});
    } else if (isVisible && !isAnimatingOut) {
      // Dialog was closed externally (e.g., after save)
      setIsVisible(false);
    }
  }, [open, user, isVisible, isAnimatingOut]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      onClose();
    }, 200);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave: Partial<User> = {
        ...formData,
        location: formData.location || undefined,
      };
      onSave(dataToSave);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`user-dialog-overlay ${isAnimatingOut ? 'user-dialog-overlay--closing' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`user-dialog ${isAnimatingOut ? 'user-dialog--closing' : ''}`}>
        {/* Header */}
        <div className="user-dialog__header">
          <div className="user-dialog__header-content">
            <div className="user-dialog__icon">
              {user ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
              )}
            </div>
            <div>
              <h2 className="user-dialog__title">{user ? 'Edit User' : 'Add New User'}</h2>
              <p className="user-dialog__subtitle">
                {user ? 'Update user information and settings' : 'Create a new team member account'}
              </p>
            </div>
          </div>
          <button className="user-dialog__close" onClick={handleClose} type="button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 5L5 15M5 5l10 10" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="user-dialog__content">
            {/* Personal Information Section */}
            <div className="user-dialog__section">
              <h3 className="user-dialog__section-title">Personal Information</h3>

              <div className="user-dialog__field">
                <label className="user-dialog__label" htmlFor="name">
                  Full Name <span className="user-dialog__required">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  className={`user-dialog__input ${errors.name ? 'user-dialog__input--error' : ''}`}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter full name"
                  autoFocus={!user}
                />
                {errors.name && <span className="user-dialog__error">{errors.name}</span>}
              </div>

              <div className="user-dialog__field">
                <label className="user-dialog__label" htmlFor="email">
                  Email Address <span className="user-dialog__required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={`user-dialog__input ${errors.email ? 'user-dialog__input--error' : ''} ${user ? 'user-dialog__input--disabled' : ''}`}
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="name@company.com"
                  disabled={Boolean(user)}
                />
                {errors.email && <span className="user-dialog__error">{errors.email}</span>}
                {user && (
                  <span className="user-dialog__hint">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="7" cy="7" r="6" />
                      <path d="M7 4v3M7 9.5v.5" />
                    </svg>
                    Email cannot be changed after creation
                  </span>
                )}
              </div>
            </div>

            {/* Role & Status Section */}
            <div className="user-dialog__section">
              <h3 className="user-dialog__section-title">Role & Status</h3>

              <div className="user-dialog__row">
                <div className="user-dialog__field user-dialog__field--half">
                  <label className="user-dialog__label" htmlFor="role">Role</label>
                  <select
                    id="role"
                    className="user-dialog__select"
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                  >
                    <option value={UserRole.T1_TECH}>T1 Tech</option>
                    <option value={UserRole.T2_TECH}>T2 Tech</option>
                    <option value={UserRole.ADMIN}>Admin</option>
                  </select>
                </div>

                <div className="user-dialog__field user-dialog__field--half">
                  <label className="user-dialog__label" htmlFor="status">Status</label>
                  <select
                    id="status"
                    className="user-dialog__select"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <option value={UserStatus.ACTIVE}>Active</option>
                    <option value={UserStatus.INACTIVE}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="user-dialog__section">
              <h3 className="user-dialog__section-title">Work Location</h3>

              <div className="user-dialog__field">
                <label className="user-dialog__label" htmlFor="location">
                  Office Location
                </label>
                <select
                  id="location"
                  className="user-dialog__select"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                >
                  <option value="">Not Assigned</option>
                  {LOCATION_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.locations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="user-dialog__hint">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M7 1C4.5 1 2.5 3 2.5 5.5C2.5 9 7 13 7 13s4.5-4 4.5-7.5C11.5 3 9.5 1 7 1z" />
                    <circle cx="7" cy="5.5" r="1.5" />
                  </svg>
                  Location is used for ticket routing and timezone settings
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="user-dialog__footer">
            <button type="button" className="user-dialog__btn user-dialog__btn--secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="user-dialog__btn user-dialog__btn--primary">
              {user ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
